import { describeError, PhotoField } from '@horof/shared';
import { Alert, App, Modal } from 'antd';
import { useState } from 'react';
import { useMutation } from 'urql';
import { UpdateMeterDocument } from './meters-data';

/**
 * Add or replace a meter's photo (`updateMeter(patch: { photoId })`). Meters
 * created before photos were required have none; a photo can be replaced but
 * not removed (arqam doc 47).
 */
export function MeterPhotoDialog({
  meterId,
  hasPhoto,
  onClose,
}: {
  meterId: string;
  hasPhoto: boolean;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [, update] = useMutation(UpdateMeterDocument);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save() {
    if (!photoId) return;
    setSaving(true);
    const result = await update(
      { id: meterId, patch: { photoId } },
      { additionalTypenames: ['Meter', 'Photo'] },
    );
    setSaving(false);
    if (result.error) {
      setProblem(
        describeError(result.error, 'تعذّر حفظ الصورة.', {
          PHOTO_WRONG_KIND: 'هذه ليست صورة عداد. التقط صورة العداد من جديد.',
          PHOTO_NOT_FOUND: 'تعذّر العثور على الصورة. التقطها من جديد.',
        }),
      );
      return;
    }
    message.success('حُفظت صورة العداد');
    onClose();
  }

  return (
    <Modal
      open
      title={hasPhoto ? 'تغيير صورة العداد' : 'إضافة صورة العداد'}
      okText="حفظ"
      cancelText="إلغاء"
      okButtonProps={{ disabled: !photoId || uploading }}
      confirmLoading={saving}
      onOk={save}
      onCancel={onClose}
    >
      <PhotoField
        kind="METER_READING"
        label="صورة العداد"
        hint="صوّر العداد بحيث يظهر رقمه وقراءته بوضوح."
        value={photoId}
        onChange={setPhotoId}
        onBusyChange={setUploading}
      />
      {hasPhoto && (
        <div className="field-hint">تحلّ الصورة الجديدة محلّ القديمة.</div>
      )}
      {problem && <Alert type="error" showIcon message={problem} />}
    </Modal>
  );
}
